import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import tournamentService from '../services/tournamentService';
import api from '../services/api';
import websocketService from '../services/websocketService';
import toast from 'react-hot-toast';
import './TournamentDetails.css';

// Import tournament tab components
import TournamentOverview from '../components/tournament/TournamentOverview';
import TournamentLeaderboard from '../components/tournament/TournamentLeaderboard';
import TournamentPlayers from '../components/tournament/TournamentPlayers';
import TournamentRounds from '../components/tournament/TournamentRounds';

const buildLeaderboardStats = (leaderboardData = []) => {
  if (!Array.isArray(leaderboardData) || leaderboardData.length === 0) {
    return {
      totalSkins: 0,
      totalSawoffs: 0,
      skinWinners: [],
      sawOffWinners: [],
      skinEvents: [],
      sawOffEvents: []
    };
  }

  const skinEventsMap = new Map();
  const sawOffEventsMap = new Map();
  const skinWinnersMap = new Map();
  const sawOffWinnersMap = new Map();
  let fallbackSkinCount = 0;
  let fallbackSawOffPoints = 0;

  const recordEvent = (eventMap, info, playerLabel) => {
    if (!info) {
      return;
    }
    const round = info.round ?? info.roundNumber ?? null;
    const hole = info.hole ?? info.holeNumber ?? null;
    const type = info.type ?? info.result ?? null;
    const key = `${round ?? 'NA'}-${hole ?? 'NA'}-${type ?? 'NA'}`;
    if (!eventMap.has(key)) {
      eventMap.set(key, {
        round,
        hole,
        type,
        winners: []
      });
    }
    eventMap.get(key).winners.push(playerLabel);
  };

  const recordWinner = (winnerMap, playerKey, playerLabel, increment, infoList = []) => {
    if (!increment || increment <= 0) {
      return;
    }
    const existing = winnerMap.get(playerKey) || {
      playerId: playerKey,
      playerName: playerLabel,
      count: 0,
      events: new Set()
    };
    existing.count += increment;
    infoList.forEach((info) => {
      const round = info?.round ?? info?.roundNumber;
      const hole = info?.hole ?? info?.holeNumber;
      if (round != null && hole != null) {
        existing.events.add(`R${round}H${hole}`);
      }
    });
    winnerMap.set(playerKey, existing);
  };

  const sanitiseInfoList = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

  leaderboardData.forEach((player) => {
    const playerName = player?.golferName || player?.name || player?.playerName || 'Unknown';
    const playerId = player?.golferId || player?.id || player?.playerId || playerName;
    const skinInfoList = sanitiseInfoList(player?.skinInfo);
    const sawOffInfoList = sanitiseInfoList(player?.sawOffInfo);

    if (skinInfoList.length > 0) {
      skinInfoList.forEach((info) => recordEvent(skinEventsMap, info, playerName));
      recordWinner(skinWinnersMap, playerId, playerName, skinInfoList.length, skinInfoList);
    } else {
      const fallbackSkins = Number(player?.totalSkins ?? player?.skins ?? 0);
      fallbackSkinCount += fallbackSkins;
      if (fallbackSkins > 0) {
        recordWinner(skinWinnersMap, playerId, playerName, fallbackSkins);
      }
    }

    if (sawOffInfoList.length > 0) {
      sawOffInfoList.forEach((info) => recordEvent(sawOffEventsMap, info, playerName));
      recordWinner(sawOffWinnersMap, playerId, playerName, sawOffInfoList.length, sawOffInfoList);
    } else {
      const fallbackSawOffs = Number(player?.totalSawOffs ?? player?.sawoffs ?? 0);
      fallbackSawOffPoints += fallbackSawOffs;
      if (fallbackSawOffs > 0) {
        recordWinner(sawOffWinnersMap, playerId, playerName, fallbackSawOffs);
      }
    }
  });

  const normaliseEvents = (eventMap) =>
    Array.from(eventMap.values())
      .map((event) => ({
        ...event,
        winners: Array.from(new Set(event.winners))
      }))
      .sort((a, b) => {
        const roundDiff = (a.round ?? 0) - (b.round ?? 0);
        if (roundDiff !== 0) {
          return roundDiff;
        }
        return (a.hole ?? 0) - (b.hole ?? 0);
      });

  const mapToArray = (winnerMap) =>
    Array.from(winnerMap.values())
      .map((entry) => ({
        playerId: entry.playerId,
        playerName: entry.playerName,
        count: entry.count,
        events: Array.from(entry.events)
      }))
      .sort((a, b) => {
        if (b.count !== a.count) {
          return b.count - a.count;
        }
        return a.playerName.localeCompare(b.playerName);
      });

  const skinEvents = normaliseEvents(skinEventsMap);
  const sawOffEvents = normaliseEvents(sawOffEventsMap);
  const skinWinners = mapToArray(skinWinnersMap);
  const sawOffWinners = mapToArray(sawOffWinnersMap);

  let totalSkins = skinEvents.length;
  if (totalSkins === 0 && skinWinners.length > 0) {
    totalSkins = skinWinners.reduce((sum, winner) => sum + winner.count, 0);
  } else if (totalSkins === 0) {
    totalSkins = fallbackSkinCount;
  }

  let totalSawoffs = sawOffEvents.length;
  if (totalSawoffs === 0 && sawOffWinners.length > 0) {
    const totalSawOffWins = sawOffWinners.reduce((sum, winner) => sum + winner.count, 0);
    totalSawoffs = Math.max(1, Math.round(totalSawOffWins / 2));
  } else if (totalSawoffs === 0 && fallbackSawOffPoints > 0) {
    totalSawoffs = Math.max(1, Math.round(fallbackSawOffPoints / 2));
  }

  return {
    totalSkins,
    totalSawoffs,
    skinWinners,
    sawOffWinners,
    skinEvents,
    sawOffEvents
  };
};

const TournamentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [tournament, setTournament] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [players, setPlayers] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Live simulation states
  const [isLiveSimulation, setIsLiveSimulation] = useState(false);
  const [currentHole, setCurrentHole] = useState({ round: 0, hole: 0 });
  const [skinsScored, setSkinsScored] = useState([]);

  const leaderboardStats = useMemo(() => buildLeaderboardStats(leaderboard), [leaderboard]);
  const playersForLeaderboard = useMemo(
    () => (leaderboard && leaderboard.length > 0 ? leaderboard : players),
    [leaderboard, players]
  );

  // Load tournament data
  useEffect(() => {
    const loadTournamentData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load tournament details and related data
        const [tournamentResult, leaderboardResult, playersResult, roundsResult] = 
          await Promise.allSettled([
            tournamentService.getTournamentById(id),
            api.get(`/scoring/tournaments/${id}/leaderboard`),
            tournamentService.getTournamentPlayers(id),
            tournamentService.getTournamentRounds(id)
          ]);

        if (tournamentResult.status === 'fulfilled' && tournamentResult.value?.success) {
          setTournament(tournamentResult.value.data);
        } else {
          throw new Error('Tournament not found');
        }

        if (leaderboardResult.status === 'fulfilled' && leaderboardResult.value?.data?.success) {
          setLeaderboard(leaderboardResult.value.data.data.leaderboard || []);
        } else {
          setLeaderboard([]);
        }

        if (playersResult.status === 'fulfilled' && playersResult.value?.success) {
          setPlayers(playersResult.value.data.players || []);
        } else {
          setPlayers([]);
        }

        if (roundsResult.status === 'fulfilled' && roundsResult.value?.success) {
          setRounds(roundsResult.value.data.rounds || []);
        } else {
          setRounds([]);
        }

      } catch (err) {
        console.error('Error loading tournament data:', err);
        setError('Unable to load tournament details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadTournamentData();
    }
  }, [id]);

  // WebSocket connection for live updates
  useEffect(() => {
      if (tournament && user?.token) {
        console.log(`🔌 Setting up WebSocket for tournament ${id}`);
        websocketService.connect(id, user.token, user.id);

      // Handle hole completion updates
      const handleHoleCompleted = (data) => {
        console.log('🏌️ Hole completed:', data);
        setCurrentHole({ round: data.data.round, hole: data.data.hole });
        setIsLiveSimulation(true);
        
        // Refresh leaderboard after hole completion
        setTimeout(() => {
          refreshLeaderboard();
        }, 1000);

        // Show hole completion notification
        const notification = {
          id: Date.now(),
          type: 'hole_completed',
          message: `Hole ${data.data.hole} completed in Round ${data.data.round}`,
          timestamp: new Date()
        };
      };

      // Handle skin scoring notifications
      const handleSkinScored = (data) => {
        console.log('🎯 Skin scored:', data);
        const playerName = data.data.playerName || data.data.golferName;
        
        // Add to skins scored for all players
        setSkinsScored(prev => [...prev, {
          id: Date.now(),
          playerName,
          round: data.data.round,
          hole: data.data.hole,
          timestamp: new Date()
        }]);

        // Show notification toast for all skin scoring
        toast.success(`🎯 ${playerName} scored a skin!`, { duration: 4000 });

      };

      // Handle leaderboard updates
      const handleLeaderboardUpdate = (data) => {
        console.log('📊 Leaderboard updated:', data);
        if (data.data?.leaderboard) {
          setLeaderboard(data.data.leaderboard);
        }
      };

      // Handle tournament completion
      const handleTournamentCompleted = (data) => {
        console.log('🏁 Tournament completed:', data);
        setIsLiveSimulation(false);
        toast.success('Tournament completed!');
        
        // Refresh all data
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      };

      // Register message handlers
      websocketService.on('HOLE_COMPLETED', handleHoleCompleted);
      websocketService.on('SKIN_SCORED', handleSkinScored);
      websocketService.on('LEADERBOARD_UPDATE', handleLeaderboardUpdate);
      websocketService.on('TOURNAMENT_COMPLETED', handleTournamentCompleted);

      // Cleanup on unmount
      return () => {
        websocketService.off('HOLE_COMPLETED', handleHoleCompleted);
        websocketService.off('SKIN_SCORED', handleSkinScored);
        websocketService.off('LEADERBOARD_UPDATE', handleLeaderboardUpdate);
        websocketService.off('TOURNAMENT_COMPLETED', handleTournamentCompleted);
        websocketService.disconnect();
      };
    }
  }, [tournament, user, id]);


  const refreshLeaderboard = async () => {
    try {
      const response = await api.get(`/scoring/tournaments/${id}/leaderboard`);
      if (response.data?.success) {
        setLeaderboard(response.data.data.leaderboard || []);
      }
    } catch (error) {
      console.error('Error refreshing leaderboard:', error);
    }
  };




  const getStatusBadge = (status) => {
    const color = tournamentService.getStatusColor(status);
    const text = tournamentService.getStatusText(status);
    
    return (
      <span 
        className="status-badge"
        style={{ 
          backgroundColor: color,
          color: 'white',
          padding: 'var(--spacing-xs) var(--spacing-sm)',
          borderRadius: 'var(--radius-sm)',
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'bold'
        }}
      >
        {text}
      </span>
    );
  };


  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <TournamentOverview tournament={tournament} leaderboardStats={leaderboardStats} />;

      case 'leaderboard':
        return (
          <TournamentLeaderboard
            players={playersForLeaderboard}
            tournament={tournament}
            totals={leaderboardStats}
            userId={user?.id}
          />
        );

      case 'players':
        return <TournamentPlayers players={players} />;

      case 'rounds':
        return <TournamentRounds rounds={rounds} tournamentId={id} />;

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="proskins-content" style={{
        padding: 'var(--spacing-lg)',
        maxWidth: '1200px',
        margin: '0 auto',
        minHeight: '100vh'
      }}>
        <div className="proskins-text-center proskins-text-muted" style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px'
        }}>
          <div className="proskins-loading" style={{ marginBottom: 'var(--spacing-sm)' }}></div>
          Loading tournament details...
        </div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="proskins-content" style={{
        padding: 'var(--spacing-lg)',
        maxWidth: '1200px',
        margin: '0 auto',
        minHeight: '100vh'
      }}>
        <div className="proskins-text-center" style={{ padding: 'var(--spacing-2xl)' }}>
          <h2 className="proskins-text-muted" style={{ marginBottom: 'var(--spacing-sm)' }}>Tournament not found</h2>
          <p className="proskins-text-muted" style={{ marginBottom: 'var(--spacing-lg)' }}>The tournament you're looking for doesn't exist.</p>
          <button
            className="proskins-btn proskins-btn-outline"
            onClick={() => navigate('/tournaments')}
          >
            ← Back to Tournaments
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="proskins-content" style={{
      padding: 'var(--spacing-lg)',
      maxWidth: '1200px',
      margin: '0 auto',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: 'var(--spacing-lg)'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 'var(--spacing-lg)',
              marginBottom: 'var(--spacing-sm)'
            }}>
              <h1 className="proskins-section-title" style={{ margin: 0 }}>{tournament.name}</h1>
              {getStatusBadge(tournament.status)}
              
              {/* Live Simulation Status */}
              {isLiveSimulation && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-xs)',
                  background: 'linear-gradient(135deg, var(--proskins-green), var(--proskins-green-dark))',
                  color: '#000',
                  padding: 'var(--spacing-xs) var(--spacing-md)',
                  borderRadius: 'var(--radius-xl)',
                  fontWeight: 'bold',
                  fontSize: 'var(--font-size-sm)',
                  marginLeft: 'var(--spacing-md)',
                  animation: 'pulse 2s infinite'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    background: '#ff4444',
                    borderRadius: '50%',
                    animation: 'blink 1s infinite'
                  }}></div>
                  <span>
                    Live - Round {currentHole.round}, Hole {currentHole.hole}
                  </span>
                </div>
              )}
            </div>
            {tournament.description && (
              <p className="proskins-text-secondary" style={{ 
                margin: 0,
                lineHeight: '1.5',
                fontSize: 'var(--font-size-lg)'
              }}>
                {tournament.description}
              </p>
            )}
          </div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 'var(--spacing-md)'
          }}>
            <button
              className="proskins-btn proskins-btn-outline"
              onClick={() => navigate('/tournaments')}
            >
              ← Back to Tournaments
            </button>
          </div>
        </div>

        {error && (
          <div className="proskins-alert proskins-alert-warning">
            {error}
          </div>
        )}

        <div className="proskins-text-secondary" style={{ 
          fontSize: 'var(--font-size-sm)',
          marginTop: 'var(--spacing-sm)'
        }}>
          Created by {tournament.creator.firstName} {tournament.creator.lastName} on{' '}
          {tournamentService.formatDate(tournament.createdAt)}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        borderBottom: '2px solid var(--border-primary)',
        marginBottom: 'var(--spacing-2xl)',
        display: 'flex',
        gap: 0
      }}>
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'leaderboard', label: 'Leaderboard' },
          { id: 'players', label: 'Golfers' },
          { id: 'rounds', label: 'Rounds' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: 'var(--spacing-md) var(--spacing-lg)',
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              borderBottom: `3px solid ${activeTab === tab.id ? 'var(--proskins-green)' : 'transparent'}`,
              color: activeTab === tab.id ? 'var(--proskins-green)' : 'var(--text-secondary)',
              fontWeight: activeTab === tab.id ? 'bold' : 'normal',
              fontSize: 'var(--font-size-base)',
              transition: 'all 0.3s ease'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ minHeight: '400px' }}>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default TournamentDetails;